import { emptyFilter, type Filter, type SavedView } from "@estelle/domain";
import { useQueries } from "@tanstack/react-query";

import { canonicalJson } from "@/components/views/canonical";
import type { ListState } from "@/components/views/SaveViewButton";
import type { useSavedViews } from "@/components/views/useSavedViews";
import { SELECTED_CHOICE } from "@/components/list/selectedChoice";
import { cn } from "@/lib/utils";

/**
 * The saved views on a phone, as tabs that scroll sideways, each with how many
 * rows it holds (#633). The desktop keeps its compact picker; on a phone a
 * dropdown hid the views behind a tap, and "To confirm 14" read from a tab is
 * the reason to open it.
 *
 * Counts come from `countOf`, the section's own count of a filter, one small
 * request per view; a section without one shows the tabs without numbers.
 */
export function ViewTabs({
  scope,
  savedViews,
  emptyLabel,
  current,
  onSelect,
  countOf,
}: {
  scope: string;
  savedViews: ReturnType<typeof useSavedViews>;
  emptyLabel: string;
  current: ListState;
  onSelect: (view: SavedView | null) => void;
  countOf?: (filter: Filter) => Promise<number | null>;
}) {
  const { views, select } = savedViews;
  const ordered = [...views].sort(
    (a, b) => Number(b.isDefault) - Number(a.isDefault),
  );

  const serialised = canonicalJson(current);
  const openId =
    views.find(
      (view) =>
        canonicalJson({
          filter: view.filter,
          columns: view.columns,
          sort: view.sort,
        }) === serialised,
    )?.id ?? null;
  const allOpen = openId === null && current.filter.children.length === 0;

  const tabs: { id: string | null; label: string; filter: Filter }[] = [
    { id: null, label: emptyLabel, filter: emptyFilter() },
    ...ordered.map((view) => ({
      id: view.id,
      label: view.name,
      filter: view.filter,
    })),
  ];

  const counts = useQueries({
    queries: tabs.map((tab) => ({
      queryKey: ["view-count", scope, canonicalJson(tab.filter)],
      queryFn: () => (countOf ? countOf(tab.filter) : Promise.resolve(null)),
      enabled: countOf !== undefined,
      staleTime: 30_000,
    })),
  });

  return (
    <div
      role="tablist"
      aria-label="Saved views"
      className="-mx-3 flex min-w-0 gap-1.5 overflow-x-auto px-3 [scrollbar-width:none]"
    >
      {tabs.map((tab, index) => {
        const active = tab.id === null ? allOpen : tab.id === openId;
        const count = counts[index]?.data;
        return (
          <button
            key={tab.id ?? "all"}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => {
              select(tab.id);
              onSelect(views.find((view) => view.id === tab.id) ?? null);
            }}
            className={cn(
              "inline-flex min-h-9 shrink-0 items-center gap-1.5 rounded-md px-3 text-sm whitespace-nowrap transition-colors",
              active
                ? SELECTED_CHOICE
                : "text-muted-foreground hover:bg-accent",
            )}
          >
            {tab.label}
            {typeof count === "number" ? (
              <span className="text-foreground font-semibold tabular-nums">
                {count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
